import { useEffect, useMemo, useState } from "react";
import type {
  PlayActionDTO,
  PlayAnswerFieldDTO,
  PlayAnswerTargetDTO,
  PlayKind,
  PlaySignal,
} from "../../api/contracts/play";
import { KIND_META, cleanReasons } from "./shared";
import { PersonPicker } from "../person-picker";
import { AnswerOptions } from "./answer-options";
import { IntakePreview } from "./intake-preview";
import {
  SetupDriver,
  type SetupCard,
  type SetupInputContext,
  type SetupPromptCard,
} from "../setup/setup-driver";
import { PlayTakeoverShell } from "./play-takeover";
import { PlayEntranceShell } from "./play-entrance";

/**
 * The Play driver — the experience the Play button opens. It maps the ranked
 * `play.queue` to the shared setup-walk card model and drives it through
 * SetupDriver, painted by the fullscreen "Screening Room" takeover
 * (`PlayTakeoverShell`, via SetupDriver's `renderShell` seam). It opens on a
 * session-aware front door (`PlayEntranceShell`): the FIRST open of a tab session
 * shows the launcher ("Start all", or jump to a lane); a RETURN resumes where the
 * user left off. Picking a set hands control to the same one-at-a-time walk.
 * `startActionKey` skips the door and opens focused on one card (the home "What to
 * do next" list, a Pulse setup CTA).
 *
 * Presentational: the host owns data fetching, the Dialog, and the callbacks.
 * The one-at-a-time walk + the answer/optimistic-save logic are the shared
 * SetupDriver primitive — never re-implemented here.
 */
export interface PlayDriverProps {
  actions: PlayActionDTO[];
  /** Total ranked actions (≥ actions.length when capped) — drives the "more queued" hint. */
  total: number;
  /**
   * Open straight onto this action (used by the home "What to do next" list and
   * the Pulse setup CTAs so the user lands on that exact card inside the one Play
   * interface). Falls back to the top of the queue when unset / unmatched.
   */
  startActionKey?: string;
  /**
   * Record a feedback signal. Reason is captured for skip / dismiss. `fieldKey`
   * names the answer field a per-field signal (skip/started) belongs to, so the
   * host can persist a per-question skip — set when an answer is walked
   * field-by-field.
   */
  onSignal: (
    action: PlayActionDTO,
    signal: PlaySignal,
    reason?: string,
    fieldKey?: string,
  ) => void;
  /**
   * Persist an inline answer (answerable cards only). `fieldKey` names the
   * field this card answers — set when an answer is walked field-by-field.
   * Resolves on success.
   */
  onAnswer?: (action: PlayActionDTO, value: string, fieldKey?: string) => Promise<void> | void;
  /**
   * Fire an action's generation trigger (action cards whose `trigger` is set —
   * "Generate questions", "Draft brief"). Resolves when the worker is spawned;
   * its output resurfaces as answerable cards on the next re-rank.
   */
  onTrigger?: (action: PlayActionDTO) => Promise<void> | void;
  /** Called when the user finishes the drive and dismisses the finish panel. */
  onExhausted?: () => void;
  /**
   * Play §2.3 action verbs, host-resolved. When provided, an ACTION card routes
   * through these instead of a raw ctaHref Link:
   *  • onDo — the Do verb (the host resolves the route + navigates).
   *  • onDive — the §2.5 Dive-in verb (open the object's owning Domain); the button
   *    shows only for cards `canDive` reports true.
   *  • onDelegate — hand the action to the agent fleet (goal creation).
   * `canDive(action)` decides Dive-in availability — the host owns the object model
   * (the resolver lives in app source, not this package).
   */
  onDo?: (action: PlayActionDTO) => void;
  onDive?: (action: PlayActionDTO) => void;
  onDelegate?: (action: PlayActionDTO) => void;
  canDive?: (action: PlayActionDTO) => boolean;
}

/** A setup card plus the action + field it maps back to, for signal routing. */
interface DriverCard {
  card: SetupCard;
  action: PlayActionDTO;
  /** The brief field this card answers (multi-field section walks). */
  fieldKey?: string;
  /** First / last sub-card of its action — collapses signals to action level. */
  isFirst: boolean;
  isLast: boolean;
}

/** The `__new__`-style option whose selection reveals AnswerOptions' free text. */
const OTHER_OPTION_ID = "__new__";

/** The select input's value is a single string: the chosen option id, or
 *  `__new__:<label>` when the free-text "New object type…" option is picked (so
 *  the intake answer route round-trips both the decision AND the type name in one
 *  string — no extra field). This encodes/decodes that convention. */
function encodeSelect(optionId: string | null, otherText: string): string {
  if (!optionId) return "";
  return optionId === OTHER_OPTION_ID ? `${OTHER_OPTION_ID}:${otherText.trim()}` : optionId;
}
function decodeSelect(value: unknown): { optionId: string | null; otherText: string } {
  const s = typeof value === "string" ? value : "";
  if (s.startsWith(`${OTHER_OPTION_ID}:`)) {
    return { optionId: OTHER_OPTION_ID, otherText: s.slice(OTHER_OPTION_ID.length + 1) };
  }
  return { optionId: s || null, otherText: "" };
}

/** The AnswerOptions input for a `type: 'select'` prompt field, bridged to the
 *  SetupDriver ctx (which owns a single string value). Its own state tracks the
 *  chosen option + the allowOther free text; both fold into the encoded ctx value. */
function SelectFieldInput({
  field,
  ctx,
}: {
  field: PlayAnswerFieldDTO;
  ctx: SetupInputContext;
}) {
  const decoded = decodeSelect(ctx.value);
  return (
    <AnswerOptions
      options={field.options ?? []}
      value={decoded.optionId}
      onChange={(id) => ctx.setValue(encodeSelect(id, decoded.otherText))}
      allowOther={field.allowOther}
      otherValue={decoded.otherText}
      onOtherChange={(t) => ctx.setValue(encodeSelect(decoded.optionId, t))}
    />
  );
}

/** Build a prompt card for one answerable brief field. */
function promptCardFor(
  action: PlayActionDTO,
  field: PlayAnswerFieldDTO,
  key: string,
  target?: PlayAnswerTargetDTO,
): SetupPromptCard {
  const isPerson = field.type === "urn-person" || field.type === "urn-people";
  const multi = field.type === "urn-people";
  // A `select` field with options renders the AnswerOptions block (the ONE
  // generic branch, §2.8.1 — no third card mode). Intake targets footer a live
  // IntakePreview so the user sees the data they're classifying.
  const isSelect = field.type === "select" && !!field.options;
  const isIntake = target?.kind === "intake";
  return {
    key,
    mode: "prompt",
    title: field.label ?? action.title,
    question: field.prompt ?? action.body ?? action.title,
    // The subhead is the explanation/examples that make the question
    // answerable, shown under it. → docs/questions/PRD.md.
    sublabel: field.subhead,
    placeholder: field.placeholder,
    initialValue: multi ? [] : "",
    // Team-member fields render the shared picker (workspace members), not
    // free text — so "who owns this?" resolves to a real identity.
    ...(isPerson
      ? {
          renderInput: (ctx) => (
            <PersonPicker
              value={ctx.value}
              multi={multi}
              onChange={ctx.setValue}
              disabled={ctx.disabled}
              placeholder={field.placeholder ?? "Select a team member…"}
              autoFocus
              // The Play driver always paints in the dark-room takeover; the
              // picker menu portals to document.body (outside that scope), so
              // re-assert the dark theme on the portaled surface.
              popoverClassName="dark"
            />
          ),
          canSave: (v: unknown) =>
            Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim().length > 0,
        }
      : isSelect
        ? {
            renderInput: (ctx: SetupInputContext) => <SelectFieldInput field={field} ctx={ctx} />,
            // A select needs a chosen option; the `__new__` free-text path also
            // needs a non-empty label after the `:`.
            canSave: (v: unknown) => {
              const { optionId, otherText } = decodeSelect(v);
              if (!optionId) return false;
              return optionId === OTHER_OPTION_ID ? otherText.trim().length > 0 : true;
            },
            ...(isIntake && target.kind === "intake"
              ? { footer: <IntakePreview batchUid={target.batchUid} /> }
              : {}),
          }
        : {}),
  } satisfies SetupPromptCard;
}

/**
 * Map a ranked action to one or more setup cards. An answerable section card
 * expands into one prompt per open field so the WHOLE setup happens inline; a
 * single-field card keeps the action's own key (feedback/dedup parity); an
 * action card stays one card with its CTA.
 */
function toCards(
  action: PlayActionDTO,
  canDive?: (a: PlayActionDTO) => boolean,
): DriverCard[] {
  if (action.answer) {
    const fields: PlayAnswerFieldDTO[] =
      action.answer.fields && action.answer.fields.length > 0
        ? action.answer.fields
        : [
            {
              fieldKey: action.answer.fieldKey,
              label: action.answer.placeholder,
              placeholder: action.answer.placeholder,
              type: action.answer.type,
            },
          ];
    return fields.map((field, i) => ({
      card: promptCardFor(
        action,
        field,
        fields.length === 1 ? action.key : `${action.key}::${field.fieldKey}`,
        action.answer!.target,
      ),
      action,
      fieldKey: field.fieldKey,
      isFirst: i === 0,
      isLast: i === fields.length - 1,
    }));
  }
  const meta = KIND_META[action.kind];
  return [
    {
      card: {
        key: action.key,
        mode: "action",
        title: action.title,
        body: action.body,
        reasons: cleanReasons(action),
        badge: { label: meta.label, intent: meta.intent },
        icon: meta.Icon,
        payoff: action.payoff,
        ctaLabel: action.ctaLabel,
        ctaHref: action.ctaHref,
        canDive: canDive ? canDive(action) : false,
        // The "Why?" causal trace (§6.5.8) — only event-derived cards carry it.
        causationUid: action.causationUid ?? undefined,
        causationKind: action.causationKind ?? undefined,
      },
      action,
      isFirst: true,
      isLast: true,
    },
  ];
}

/* ── Session memory (per tab session) ───────────────────────────────────────
 * `entered` gates the launcher: absent → cold (first open → launcher); set on the
 * first Start/lane/resume choice → warm (next open resumes). `cursor` is the last
 * action shown — where a warm reopen picks up. sessionStorage clears on tab close,
 * so each new session starts cold. Best-effort + SSR-safe. */
const SESSION_ENTERED = "excellent.play.entered";
const SESSION_CURSOR = "excellent.play.cursor";

function readPlaySession(): { entered: boolean; cursor: string | null } {
  if (typeof window === "undefined") return { entered: false, cursor: null };
  try {
    return {
      entered: window.sessionStorage.getItem(SESSION_ENTERED) === "1",
      cursor: window.sessionStorage.getItem(SESSION_CURSOR),
    };
  } catch {
    return { entered: false, cursor: null };
  }
}

function markPlayEntered(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_ENTERED, "1");
  } catch {
    /* private mode / storage disabled — entrance just stays cold */
  }
}

function writePlayCursor(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_CURSOR, key);
  } catch {
    /* best-effort */
  }
}

export function PlayDriver({
  actions,
  total,
  startActionKey,
  onSignal,
  onAnswer,
  onTrigger,
  onExhausted,
  onDo,
  onDive,
  onDelegate,
  canDive,
}: PlayDriverProps) {
  // ── Session-aware front door ──────────────────────────────────────────────
  // The FIRST open of a tab session shows the launcher (cold); a RETURN resumes
  // where the user left off (warm). A focused open (startActionKey, e.g. the home
  // "what to do next" list / a Pulse setup CTA) skips the door and lands on that
  // card. The walk machinery below is unchanged; the door just chooses its set.
  const session = useMemo(() => readPlaySession(), []);
  const [phase, setPhase] = useState<"entrance" | "walk">(
    startActionKey ? "walk" : "entrance",
  );
  const [filterKind, setFilterKind] = useState<PlayKind | null>(null);
  const [seekKey, setSeekKey] = useState<string | null>(startActionKey ?? null);

  // Where a returning user left off — the cursor action's ranked position. Counts
  // are framed against `total` (the full ranked count) so the resume copy stays
  // consistent with "of N" even when the loaded set is capped below total.
  const resume = useMemo(() => {
    const idx = session.cursor ? actions.findIndex((a) => a.key === session.cursor) : -1;
    return idx < 0
      ? { key: null as string | null, index: 0, remaining: total }
      : { key: session.cursor, index: idx, remaining: total - idx };
  }, [actions, session.cursor, total]);

  // Open on the resume door only when there's a real place to resume to.
  const [door, setDoor] = useState<"cold" | "warm">(
    session.entered && resume.key ? "warm" : "cold",
  );

  // A focused open counts as entering, so the next open of the session is warm.
  useEffect(() => {
    if (startActionKey) markPlayEntered();
  }, [startActionKey]);

  // The set this drive walks — all of it, or one chosen lane.
  const view = useMemo(
    () => (filterKind ? actions.filter((a) => a.kind === filterKind) : actions),
    [actions, filterKind],
  );
  // Expand to the flat card list + a back-map (card key → action + field) so the
  // shared driver's callbacks can persist + re-rank.
  const driverCards = useMemo(() => view.flatMap((a) => toCards(a, canDive)), [view, canDive]);
  const cards = useMemo(() => driverCards.map((d) => d.card), [driverCards]);
  const metaByKey = useMemo(
    () => new Map(driverCards.map((d) => [d.card.key, d])),
    [driverCards],
  );

  // Seek onto a specific card (focused open / resume / top of a lane).
  const initialIndex = useMemo(() => {
    if (!seekKey) return 0;
    const i = driverCards.findIndex((d) => d.action.key === seekKey);
    return i < 0 ? 0 : i;
  }, [driverCards, seekKey]);

  // Entrance → walk transitions (each marks the session "entered" for next time).
  const startAll = () => {
    markPlayEntered();
    setFilterKind(null);
    setSeekKey(null);
    setPhase("walk");
  };
  const startLane = (kind: PlayKind) => {
    markPlayEntered();
    setFilterKind(kind);
    setSeekKey(null);
    setPhase("walk");
  };
  const continueResume = () => {
    markPlayEntered();
    setFilterKind(null);
    setSeekKey(resume.key);
    setPhase("walk");
  };
  // Back from the first card of the walk (or "Pick another" on finish) returns to
  // the launcher — the old `onRestart`, restored by the entrance.
  const restart = () => {
    setFilterKind(null);
    setSeekKey(null);
    setDoor("cold");
    setPhase("entrance");
  };

  // Empty queue: no door — drop straight to the walk's caught-up empty state.
  if (actions.length === 0) {
    return (
      <SetupDriver
        cards={[]}
        onExhausted={onExhausted}
        renderShell={(ctx) => <PlayTakeoverShell ctx={ctx} />}
      />
    );
  }

  // The session-aware front door.
  if (phase === "entrance") {
    return (
      <PlayEntranceShell
        key={door}
        actions={actions}
        total={total}
        mode={door}
        resumeIndex={resume.index + 1}
        remaining={resume.remaining}
        onStartAll={startAll}
        onStartLane={startLane}
        onContinue={continueResume}
        onClose={() => onExhausted?.()}
      />
    );
  }

  return (
    <SetupDriver
      key={`${filterKind ?? "all"}:${seekKey ?? ""}`}
      cards={cards}
      total={total}
      initialIndex={initialIndex}
      renderShell={(ctx) => <PlayTakeoverShell ctx={ctx} />}
      onRestart={restart}
      onSignal={(card, signal, reason) => {
        const d = metaByKey.get(card.key);
        if (!d) return;
        // Track the cursor — the action currently shown is where a warm reopen
        // resumes. Write before the multi-field collapse so every sub-card maps
        // back to its single action key.
        if (signal === "shown") writePlayCursor(d.action.key);
        // A multi-field answer is ONE action to the ledger: count the impression
        // once (first field) and completion once (last field); pass the rest
        // (started / skip / dismiss) straight through.
        if (signal === "shown" && !d.isFirst) return;
        if (signal === "completed" && !d.isLast) return;
        // An action card whose CTA is a generation trigger: "started" fires the
        // async worker. (Still recorded as engagement below.)
        if (signal === "started" && d.action.trigger) onTrigger?.(d.action);
        // Pass the field key so the host can persist a per-question skip.
        onSignal(d.action, signal, reason, d.fieldKey);
      }}
      onAnswer={async (card, value) => {
        const d = metaByKey.get(card.key);
        if (!d) return;
        // Multi-person picks arrive as a string[] — persist as JSON so the
        // brief field round-trips back to an array on read.
        const serialized = Array.isArray(value)
          ? JSON.stringify(value)
          : typeof value === "string"
            ? value
            : String(value);
        await onAnswer?.(d.action, serialized, d.fieldKey);
      }}
      onExhausted={onExhausted}
      onAct={
        onDo
          ? (card) => {
              const d = metaByKey.get(card.key);
              if (d) onDo(d.action);
            }
          : undefined
      }
      onDive={
        onDive
          ? (card) => {
              const d = metaByKey.get(card.key);
              if (d) onDive(d.action);
            }
          : undefined
      }
      onDelegate={
        onDelegate
          ? (card) => {
              const d = metaByKey.get(card.key);
              if (d) onDelegate(d.action);
            }
          : undefined
      }
    />
  );
}
